/**
 * The fighter state machine. Knows nothing about combat, rendering or input —
 * it holds the current state, the rules for leaving it, and the clip name the
 * animation layer should be playing. Combat code decides *what* to request;
 * this decides whether the request is legal.
 *
 * The rules live in states.json so the state graph can be read and edited as
 * data. Each state declares:
 *
 *   clip           the animation clip name for this state
 *   loop           whether that clip loops
 *   interruptible  whether an outside request can cut the state short
 *   to             the states reachable from here; anything else is refused
 *   onClipEnd      optional state to fall into when the clip finishes
 *   fade           optional crossfade into this state's clip, in seconds.
 *                  Locomotion blends; a reaction has to snap, or a flinch
 *                  shorter than the default blend never fully arrives.
 *   faces          whether the fighter keeps turning to their opponent in this
 *                  state. Default false: a state that does not declare it
 *                  freezes the fighter's aim for as long as it lasts, which is
 *                  what stops a thrown punch from tracking its target.
 */

export class StateConfigError extends Error {}

/** Throws on a graph that cannot be satisfied, so a typo fails loudly at boot. */
export function validateConfig(config) {
  const { initial, states } = config ?? {};
  if (!states || typeof states !== 'object') {
    throw new StateConfigError('config.states must be an object');
  }
  const names = Object.keys(states);
  if (!names.includes(initial)) {
    throw new StateConfigError(`initial state "${initial}" is not defined`);
  }

  for (const [name, state] of Object.entries(states)) {
    if (!state.clip) throw new StateConfigError(`state "${name}" has no clip`);
    if (!Array.isArray(state.to)) {
      throw new StateConfigError(`state "${name}" has no "to" array`);
    }
    for (const target of state.to) {
      if (!names.includes(target)) {
        throw new StateConfigError(`state "${name}" points at unknown state "${target}"`);
      }
    }
    if (state.fade !== undefined && !(state.fade >= 0)) {
      throw new StateConfigError(`state "${name}" has a fade of ${state.fade}`);
    }
    if (state.faces !== undefined && typeof state.faces !== 'boolean') {
      throw new StateConfigError(`state "${name}" has a non-boolean "faces"`);
    }
    if (state.onClipEnd && !names.includes(state.onClipEnd)) {
      throw new StateConfigError(
        `state "${name}" ends in unknown state "${state.onClipEnd}"`
      );
    }
    if (state.onClipEnd && !state.to.includes(state.onClipEnd)) {
      throw new StateConfigError(
        `state "${name}" ends in "${state.onClipEnd}", which is not in its "to" list`
      );
    }
  }
  return config;
}

export function createStateMachine(config, { onChange } = {}) {
  validateConfig(config);

  const states = config.states;
  let name = config.initial;
  let timeInState = 0;
  let clipDone = false;
  /**
   * Seconds something outside is keeping this fighter where they are.
   *
   * A held fighter's clip can finish without the state ending. Nothing else
   * changes: a blow still lands, `request` is still answered, and `force` still
   * goes wherever it is told — a hold stops a fighter *getting up*, it does not
   * make them invulnerable or take the round away from the match.
   */
  let held = 0;
  /**
   * The clip finished while the fighter was being held.
   *
   * It has to be remembered, because a `finished` event arrives **once**. Drop
   * it and the state has no way out left: the mixer will not fire again, and a
   * fighter held down for a second stays on the floor for the rest of the
   * round. Found exactly that way.
   */
  let endedWhileHeld = false;
  const listeners = new Set();
  if (onChange) listeners.add(onChange);

  function definition(which = name) {
    return states[which];
  }

  function enter(next, reason) {
    const from = name;
    name = next;
    timeInState = 0;
    clipDone = false;
    endedWhileHeld = false;
    for (const listener of listeners) listener({ from, to: next, reason, state: definition() });
  }

  /**
   * Lets go, and pays out a clip end that arrived while the fighter was held.
   *
   * Returns whether the state actually changed, which is what tells a caller
   * the fighter is back on their feet rather than merely released.
   */
  function settle() {
    if (held > 0 || !endedWhileHeld) return false;
    endedWhileHeld = false;
    clipDone = true;
    const next = definition().onClipEnd;
    if (!next) return false;
    enter(next, 'clipEnd');
    return true;
  }

  /**
   * Why a transition would be refused, or null if it is allowed.
   * Separated out so the debug overlay can explain a greyed-out button.
   */
  function refusal(next, { ignoreInterrupt = false } = {}) {
    if (!states[next]) return `no such state "${next}"`;
    // Re-entering a state is a no-op unless the graph explicitly allows it —
    // stagger lists itself, so a second hit can restart the flinch.
    if (next === name && !definition().to.includes(name)) return 'already in that state';
    if (!definition().to.includes(next)) return `${name} cannot move to ${next}`;
    if (!definition().interruptible && !clipDone && !ignoreInterrupt) {
      return `${name} is not interruptible`;
    }
    return null;
  }

  return {
    get state() {
      return name;
    },
    get definition() {
      return definition();
    },
    get clip() {
      return definition().clip;
    },
    get loop() {
      return definition().loop !== false;
    },
    get timeInState() {
      return timeInState;
    },
    get clipDone() {
      return clipDone;
    },

    states: () => Object.keys(states),
    get: (which) => states[which],

    can: (next, options) => refusal(next, options) === null,
    refusal,

    /** Legal transition. Returns false, without changing state, when refused. */
    request(next, options) {
      if (refusal(next, options)) return false;
      enter(next, 'request');
      return true;
    },

    /**
     * Skips every rule. This is the test and debug door — combat code should
     * use request() so illegal moves surface instead of silently working.
     */
    force(next) {
      if (!states[next]) return false;
      // Forcing clears a hold. This is the reset door — the match uses it to
      // put a fighter back on their mark — and a fighter restored into `idle`
      // while still pinned would be stuck there for the rest of the round.
      held = 0;
      endedWhileHeld = false;
      enter(next, 'force');
      return true;
    },

    /**
     * Called when the current state's clip finishes. Falls into onClipEnd if
     * the state declares one; otherwise just unlocks an uninterruptible state,
     * so a finished animation stops blocking the next move.
     */
    /** True while something outside is stopping this fighter getting up. */
    get held() {
      return held > 0;
    },
    get heldFor() {
      return Math.max(0, held);
    },

    /**
     * Keeps the fighter in the state they are in, for a while.
     *
     * The crowd reaching over the barrier and pinning somebody to the floor is
     * the only thing that does this, and it does it by suppressing exactly one
     * thing: the state's *own* exit when its clip runs out. Longest wins, so a
     * second pair of hands cannot shorten the first.
     */
    hold(seconds) {
      if (seconds > held) held = seconds;
      return held;
    },
    release() {
      held = 0;
      return settle();
    },

    clipEnded() {
      // Held: the clip is over and they are staying where they are. `clipDone`
      // stays false too, because that is what unlocks an uninterruptible state
      // — a fighter being held down has not finished being knocked down. Kept
      // rather than dropped, and spent the moment the hands let go.
      if (held > 0) {
        endedWhileHeld = true;
        return false;
      }
      clipDone = true;
      const next = definition().onClipEnd;
      if (next) {
        enter(next, 'clipEnd');
        return true;
      }
      return false;
    },

    update(dt) {
      timeInState += dt;
      if (held > 0) {
        held = Math.max(0, held - dt);
        if (held === 0) settle();
      }
    },

    onChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
